/**
 * Host-shell detection (mobile / desktop / web).
 *
 * The body class is set in ``main.tsx`` by the host classifier;
 * these helpers expose the same information to React components and
 * non-React modules. ``isCapacitor()`` is the load-bearing check —
 * it gates the mobile-only UI (annotators + reviewers, no
 * management) and the per-project ``allow_on_mobile`` filter.
 *
 * Why duplicate the body class with helpers? CSS rules can use the
 * class directly (``body.is-capacitor .x { display: none }``), but
 * JS needs a one-line predicate to skip rendering management menu
 * items, gate API filters, etc. Both layers read the same source of
 * truth.
 */
import { useState } from "react";

export const isCapacitor = (): boolean => {
  if (typeof window === "undefined") return false;
  // ``window.Capacitor`` is set by the WebView bridge before any
  // React code runs. ``body.is-capacitor`` is the CSS contract; we
  // OR them so a stale-state body without Capacitor still falls back
  // to the runtime check.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (window as any).Capacitor !== "undefined") return true;
  if (typeof document !== "undefined" && document.body?.classList?.contains("is-capacitor")) return true;
  return false;
};

export const isTauri = (): boolean => {
  if (typeof window === "undefined") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (typeof w.__TAURI__ !== "undefined") return true;
  if (typeof w.__TAURI_INTERNALS__ !== "undefined") return true;
  if (typeof document !== "undefined" && document.body?.classList?.contains("is-tauri")) return true;
  return false;
};

export const isWeb = (): boolean => !isCapacitor() && !isTauri();

/** React hook form. Stable across re-renders. */
export const useIsCapacitor = (): boolean => {
  const [v] = useState<boolean>(() => isCapacitor());
  return v;
};

/** React hook form. Stable across re-renders. */
export const useIsTauri = (): boolean => {
  const [v] = useState<boolean>(() => isTauri());
  return v;
};
